import { useCallback, useState } from "react"
import useErrorPopup from "../../hooks/useErrorPopup"
import type { StoredGeneratedAnsweredQuestion } from "../../types/jobs"
import { getUserFacingErrorType } from "../../userFacingErrorHelpers"
import { reviewAnswer } from "./utils"

interface Result {
  approving: boolean
  handleApproveMany: (
    answers: StoredGeneratedAnsweredQuestion[],
    skipContentCheck: boolean,
  ) => Promise<void>
  handleUnapproveMany: (
    selectedAnswers: StoredGeneratedAnsweredQuestion[],
  ) => Promise<void>
}

export const useApprovalHandlers = (jobOid: string): Result => {
  const [approving, setApproving] = useState(false)
  const { handleError } = useErrorPopup()

  const handleApproveMany = useCallback(
    async (
      answers: StoredGeneratedAnsweredQuestion[],
      skipContentCheck: boolean,
    ) => {
      try {
        setApproving(true)

        await reviewAnswer("REVIEW", jobOid, answers, skipContentCheck)
      } catch (error) {
        if (getUserFacingErrorType(error) === "OUT_OF_DATE") {
          handleError({
            error,
            message: (
              <>
                One of the answers is out of date.
                <br />
                Save the update or refresh and reread to approve
              </>
            ),
            duration: 6000,
          })
        } else {
          handleError({ error, prefix: "Couldn't approve answers" })
        }
        throw error
      } finally {
        setApproving(false)
      }
    },
    [jobOid, handleError],
  )

  const handleUnapproveMany = useCallback(
    async (selectedAnswers: StoredGeneratedAnsweredQuestion[]) => {
      setApproving(true)
      try {
        await reviewAnswer("UNREVIEW", jobOid, selectedAnswers)
      } catch (error) {
        handleError({ error, prefix: "Couldn't unapprove selected answers" })
        throw error
      } finally {
        setApproving(false)
      }
    },
    [jobOid, handleError],
  )

  return {
    approving,
    handleApproveMany,
    handleUnapproveMany,
  }
}
